import { Component, ContentChild, Input, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { CardNestedCardDirective } from '../../../../shared/cards/card.directive';
import { AnimatedDirective } from '../../../../shared/directives/animated.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { LegfiPhonePipeModule } from '../../../../../../pipes/format/legfi-phone.module';
import { MatDividerModule } from '@angular/material/divider';
import { FormArray, FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { NgxMaskModule } from 'ngx-mask';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { PHONE_VALIDATION_MASK } from '../../../../../../directives/validators/string/phone-validator.directive';
import { RequiredMarkerDirective, SelectAllDirective } from '../../../../shared/directives/form.directive';
import { CapitalizePipeModule } from '../../../../../../pipes/format/capitalize.module';
import { MatRadioModule } from '@angular/material/radio';
import { ButtonDirective } from '../../../../shared/directives/button.directive';
import { I18nPluralPipe, NgForOf, NgIf } from '@angular/common';
import { validateRecursive } from '../../../../../../directives/validators/validate-recursive';
import { GrowlerService } from '../../../../../../services/growler.service';
import { ControlMessagesModule } from '../../../../../shared/control-messages/control-messages.module';
import { MatSelectModule } from '@angular/material/select';
import { TeamMemberSearchComponent } from '../../team-member-search/team-member-search.component';
import { User } from '../../../../../../models/entities/user';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InfoTooltipDirective } from '../../../../shared/directives/tooltip.directive';
import { OrganizationDataService } from '../../../../../../services/organization/organization-data.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SimpleOrganization } from '../../../../../../services/organization/organization.service';
import { I18nMap } from '../../../../../../models/entities/i18n-map';
import { OrLineDirective } from '../../../../shared/directives/list.directive';

export interface TeamMemberValue
{
    id: number | null;
    fullName: string;
    email: string;
    role: string;
    organizations: number[];
    phone: string;
    emailNotification: boolean;
}

export interface TeamMemberFormGroup
{
    id: FormControl<number>;
    fullName: FormControl<string>;
    email: FormControl<string>;
    role: FormControl<string>;
    organizations: FormControl<SimpleOrganization[]>;
    phone: FormControl<string>;
    emailNotification: FormControl<boolean>;
    isEditing: FormControl<boolean>;
    isSelected: FormControl<boolean>;
}

@UntilDestroy()
@Component({
    selector: 'app-team-member-add-card',
    styleUrls: ['./team-member-add-card.component.scss'],
    templateUrl: './team-member-add-card.component.html',
    standalone: true,
    imports: [
        MatCardModule,
        CardNestedCardDirective,
        AnimatedDirective,
        MatFormFieldModule,
        MatIconModule,
        MatAutocompleteModule,
        LegfiPhonePipeModule,
        MatDividerModule,
        ReactiveFormsModule,
        MatInputModule,
        NgxMaskModule,
        MatButtonModule,
        MatListModule,
        RequiredMarkerDirective,
        CapitalizePipeModule,
        MatRadioModule,
        NgForOf,
        ControlMessagesModule,
        MatSelectModule,
        NgIf,
        MatTooltipModule,
        InfoTooltipDirective,
        ButtonDirective,
        I18nPluralPipe,
        SelectAllDirective,
        OrLineDirective,
    ],
})
export class TeamMemberAddCardComponent implements OnInit
{
    @Input() formGroup: FormGroup<{ admins: FormArray<FormGroup<TeamMemberFormGroup>>; [prop: string]: any; }>;
    @Input() showOrganizationSelect = false;

    @ContentChild(TeamMemberSearchComponent) search!: TeamMemberSearchComponent;

    organizationsList: SimpleOrganization[];

    readonly organizationsMapping: I18nMap = {
        '=0': 'No organizations',
        '=1': '1 organization',
        'other': '# organizations',
    };
    readonly phoneNumberValidationMask = PHONE_VALIDATION_MASK;
    readonly roleTooltip = `
        Property Manager – Full access to the PMC dashboard and all managed communities.
        \nCommunity Manager – Limited access to specific HOA communities only.
    `;

    constructor(
            private _fb: NonNullableFormBuilder,
            private _organizationDataService: OrganizationDataService,
            private _growler: GrowlerService,
    ) {
    }

    get admins() {
        return this.formGroup?.get('admins') as FormArray<FormGroup<TeamMemberFormGroup>>;
    }

    get showSearch() {
        if (this.admins?.controls.length > 0) {
            // if we are adding/editing at least one active admin, display search with "or" line
            return !!this.admins.controls.find((control) => {
                return !!control.get('isEditing').value;
            });
        }

        return false;
    }

    ngOnInit() {
        this._organizationDataService.orgList$.pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.organizationsList = res;
                for (const group of this.admins.controls) {
                    if (this.organizationsList.length > 1) {
                        group.get('organizations').enable();
                        group.get('organizations').addValidators(Validators.required);
                    }

                    if (this.organizationsList.length > 0 && !group.get('organizations').value) {
                        group.get('organizations').setValue([this.organizationsList[0]]);
                    }
                }
            },
        });
    }

    addMember(existingUser?: User) {
        if (existingUser) {
            const alreadySelected = !!this.admins.getRawValue().find((admin) => {
                return admin.email === existingUser.email;
            });

            if (!alreadySelected) {
                const organizationIds = existingUser.memberships.map((membership) => membership.organizationId);
                const preselectedOrganizations = this.organizationsList.filter((organization) => {
                    return organizationIds.indexOf(organization.id) > -1;
                });

                this.admins.push(this._fb.group({
                    id: [existingUser.id],
                    fullName: [existingUser.name, Validators.required],
                    email: [existingUser.email, Validators.required],
                    role: [existingUser.propertyManagementCompanyUser ? 'pmcUser' : 'admin'],
                    organizations: [preselectedOrganizations, Validators.required],
                    phone: [existingUser.memberships[0].profile.phone],
                    emailNotification: [false],
                    isEditing: [false],
                    isSelected: [true],
                }));

                this.search?.selectedEmails.select(existingUser.email);
            }
        } else {
            this.admins.push(this._fb.group({
                id: [null],
                fullName: ['', Validators.required],
                email: ['', Validators.required],
                role: ['admin'],
                organizations: [{ value: [this.organizationsList[0]], disabled: this.organizationsList?.length < 2 }, Validators.required],
                phone: [''],
                emailNotification: [true],
                isEditing: [true],
                isSelected: [true],
            }));
        }
    }

    cancelMember(index: number) {
        this.search?.selectedEmails.deselect(this.admins.at(index).getRawValue().email);
        this.admins.removeAt(index);
    }

    saveMember(index: number) {
        const group = this.admins.at(index);
        validateRecursive(group);

        if (group.invalid) {
            this._growler.oops('Please double check all required fields');
            return;
        }

        this.search?.selectedEmails.select(this.admins.at(index).getRawValue().email);

        group.get('isEditing').setValue(false);
    }
}
